import { useSelector, useDispatch } from 'react-redux'
import IconButton from '@mui/joy/IconButton';
import {MathJax} from "better-react-mathjax";
import { useState,  useEffect } from 'react';

import Toast from './Toast';
import Button from '@mui/joy/Button';
import Stack from '@mui/joy/Stack';
import { Modal, Tooltip, Sheet, Avatar, Badge, Input, Switch} from '@mui/joy';
import SpinSelector from './SpinSelector';
import FormControlLabel from '@mui/material/FormControlLabel';
import ParitySelector from './ParitySelector';

import PDGSearchbar from './PDGSearchbar';
import get_jp from '../Internal/QuantumNumbers';
import { Delete } from '@mui/icons-material';

function DeleteBadge({ disabled, onDelete }) {
    const [hovered, setHovered] = useState(false);
    return (
        <Tooltip title={disabled ? "Minimum 3 particles" : "Remove particle"} placement="top">
            <span
                onClick={onDelete}
                onMouseEnter={() => !disabled && setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    zIndex: 10,
                    width: '1.4rem',
                    height: '1.4rem',
                    borderRadius: '50%',
                    backgroundColor: disabled ? '#aaa' : (hovered ? '#a51111' : '#c41c1c'),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    userSelect: 'none',
                    transform: hovered && !disabled ? 'scale(1.2)' : 'scale(1)',
                    transition: 'transform 0.1s, background-color 0.1s',
                    boxShadow: hovered && !disabled ? '0 0 0 2px rgba(196,28,28,0.3)' : 'none',
                }}
            >
                <Delete style={{ fontSize: '0.85rem', display: 'block' }} />
            </span>
        </Tooltip>
    );
}

export default function FinalStateNode({node, decaying=false, onDelete=null, deleteDisabled=false}) {
    const n = node;
    const { spin: j, parity: p, name: nm } = useSelector((state) => state.finalState.finalStateData[n]);
    const finalStateData = useSelector(state => state.finalState.finalStateData);
    const locked = useSelector(state => state.decayLock);
    const dispatch = useDispatch();

    const [showDetails, setShowDetails] = useState(false);
    const [spin, setSpin] = useState(j || 0);
    const [parity, setParity] = useState(p || 1);
    const [name, setName] = useState(nm || n);
    const [parityConserved, setParityConserved] = useState(false);

    const [messageOpen, setMessageOpen] = useState(false);

    const checkIdenticalParticles = (stateData) => {
        const nameParticleList =  Object.keys(stateData).map((key) => {
            return stateData[key].name;
            });
        const uniqueNames = new Set(nameParticleList);
        const issues = [];
        const particleGroups = {};
        if (uniqueNames.size === nameParticleList.length) {
            issues.push("NoIdenticalParticles");
            Object.keys(stateData).forEach((key) => {
                particleGroups[stateData[key].name] = [key];
            });

        } else {
            // the first name is the mother. This particle has to be unique
            if (nameParticleList.reduce((acc, name) => (name === nameParticleList[0] ? acc + 1 : acc), 0) > 1) {
                issues.push("MotherNotUnique")
            }
            for (const name of uniqueNames) {
                const particlesWithName = Object.keys(stateData).filter((key) => stateData[key].name === name);
                const firstParticle = stateData[particlesWithName[0]];
                particleGroups[name] = [...particlesWithName];
                if (!particlesWithName.every((key) => stateData[key].spin === firstParticle.spin && stateData[key].parity === firstParticle.parity)) {
                    issues.push("IdenticalParticlesDifferentQM");
                }
            }
            if (issues.length === 0)
                issues.push("IdenticalParticles");
            
        }

        console.log( {
            issues: issues,
            particleGroups: particleGroups
        });
        return {
            issues: issues,
            particleGroups: particleGroups
        };
    }
    const updateState = (stateData) => {
        dispatch( { type: "CLEAR_MESSAGE", payload: "MotherNotUnique" });
        dispatch( { type: "CLEAR_MESSAGE", payload: "IdenticalParticles" });
        dispatch( { type: "CLEAR_MESSAGE", payload: "IdenticalParticlesDifferentQM" });

        const identicalParticleStatus = checkIdenticalParticles(stateData);
        // check if array contains keywords
        if (identicalParticleStatus.issues.includes("NoIdenticalParticles")) {
            dispatch({ type:"SET_IDENTICAL_PARTICLES", payload: {}});
            return;
        }
        if (identicalParticleStatus.issues.includes("IdenticalParticles")) {
            dispatch({ type:"SET_MESSAGE", payload: {"IdenticalParticles": {"message": "Identical particles detected. Symmetrization will be handeled by the backend!", "title": "Identical Particles", "color": "success"} }});
        }

        if (identicalParticleStatus.issues.includes("MotherNotUnique")) {
            dispatch({ type:"SET_MESSAGE", payload: {"MotherNotUnique": {"message": "The mother particle has to be unique! Please change the name of one of the particles.", "title": "Mother Particle not Unique", "color": "danger"} }});
        }

        if (identicalParticleStatus.issues.includes("IdenticalParticlesDifferentQM")) {
            dispatch({ type:"SET_MESSAGE", payload: {"IdenticalParticlesDifferentQM": {"message": "Particles with identical name but different quantum numbers detected. Please check your input!", "title": "Identical Particles with different Quantum Numbers", "color": "danger"} }});
        }
        dispatch({ type:"SET_IDENTICAL_PARTICLES", payload: identicalParticleStatus.particleGroups });
        // dispatch({ type: "SET_BROTHER_TREES", payload: identicalParticleStatus.brotherTrees });

    };


    useEffect(() => {
        setName(nm);
    }, [nm]);
    
    const handleShowDetails = () => {
        setShowDetails(!showDetails);
    }

    const handleSpinChange = (value) => {
        setSpin(value);
    }

    const handleParityChange = (value) => {
        setParity(value);
    }

    const handleNameChange = (value) => {
        setName(value);
    }

    const handleQMChange = (e) => {
         const updatedState = {
            ...finalStateData,
            [n]: {
                ...finalStateData[n],
                spin,
                parity,
                name,
                parityConserved: (decaying ? parityConserved : null)
            },
        };

        dispatch({ type: 'SET_FINAL_STATE_DATA', payload: updatedState });
        // updateState(updatedState);
        setShowDetails(false);
    }

    updateState(finalStateData);

    const base_string = get_jp(j, p); // we need this here to force re-rendering of the MathJax component
    return (
        <div>
            {
                messageOpen ? <Toast message="Parity not defined! Select parity by hand. If particle will be produced by weak interaction, set conserve parity to False in corresponding resonances." open={messageOpen} setOpen={setMessageOpen}/>
                : null
}
            <Stack direction="column" spacing={0.5} alignItems="center" sx={{padding: "0.5rem"}}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <Tooltip title={"Click to change spin and parity of particle " + n}>
                    <IconButton onClick={handleShowDetails} disabled={locked}>
                        <Badge color="primary" variant="solid" badgeContent={name} anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}>
                        <Badge color="primary" variant="solid" badgeContent={n.toString()} anchorOrigin={{vertical: 'top', horizontal: 'left'}}>
                        <Avatar variant="soft" color="primary" sx={{ width: "4rem", height: "4rem" }}>
                            <MathJax key={base_string}>
                                {base_string}
                            </MathJax>
                        </Avatar>
                        </Badge>
                        </Badge>
                    </IconButton>
                </Tooltip>
                {onDelete && (
                    <DeleteBadge
                        disabled={deleteDisabled || locked}
                        onDelete={(e) => { e.stopPropagation(); if (!deleteDisabled && !locked) onDelete(); }}
                    />
                )}
            </div>
            </Stack>
            
                <Modal open={showDetails && !locked} onClose={handleShowDetails} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Sheet
                    variant="outlined"
                    sx={{ maxWidth: 500, borderRadius: 'md', p: 3, boxShadow: 'lg' }}>
                   <Stack>
                    <SpinSelector value={spin} onChange={(e, newValue) => handleSpinChange(newValue)} locked={locked} toolTip={"Change the spin of the particle"} />
                    <ParitySelector value={parity} onChange={(e, newValue) => handleParityChange(newValue)} locked={locked} toolTip={"Change the parity of the particle"} />
                    <Tooltip title={"Change the name of the particle"}>
                    <Input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="Name" disabled={locked} />
                    </Tooltip>
                   <Button onClick={handleQMChange} disabled={locked}>Change</Button>
                   <PDGSearchbar
                    onParticleSelect={(newValue) => {
                            if (newValue) {
                                handleNameChange(newValue.label);
                                handleSpinChange(newValue.spin);
                                if (newValue.parity != 0) {
                                    console.log(newValue.parity);
                                    handleParityChange(newValue.parity);
                                }
                                else {
                                    handleParityChange(null);
                                    setMessageOpen(true);
                                }
                            }
                        }
                    }
                />
                { decaying ?
                <FormControlLabel label={"Parity Conservation"} control={
                        <Switch checked={parityConserved} onChange={(e) => {setParityConserved(e.target.checked)}}
                sx={ {Items: 'center', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '1.5rem'} }/>}
                /> : null
                }

               </Stack>
            </Sheet>
            </Modal>
        </div>
    )
}